import type { Bi } from './sectors'

/* Answer-first questions for the platform overview. Every answer restates
   positioning already published elsewhere on the site — no new claims. */
export type FaqItem = { id: string; q: Bi; a: Bi; link?: { to: string; label: Bi } }

export const platformFaqCopy = {
  eyebrow: { fr: 'Questions fréquentes', en: 'Frequently asked questions' } as Bi,
  title: { fr: 'BCITI | ATNA, en bref', en: 'BCITI | ATNA at a glance' } as Bi,
}

export const platformFaq: FaqItem[] = [
  {
    id: 'what',
    q: { fr: 'Qu’est-ce que BCITI | ATNA ?', en: 'What is BCITI | ATNA?' },
    a: {
      fr: 'BCITI | ATNA est une plateforme qui relie l’identité, les systèmes, les données et l’intelligence d’une organisation autour d’un même contexte de confiance. Elle comprend vos processus, connecte ce qui existe et y intègre l’intelligence pour simplifier les opérations et créer plus de capacité — pour vos équipes comme pour vos usagers.',
      en: 'BCITI | ATNA is a platform that connects an organization’s identity, systems, data and intelligence around one trusted context. It understands your processes, connects what already exists and integrates intelligence to simplify operations and create more capacity — for your teams and the people you serve.',
    },
  },
  {
    id: 'atna',
    q: { fr: 'Qu’est-ce qu’ATNA ?', en: 'What is ATNA?' },
    a: {
      fr: 'ATNA est la couche d’intelligence de la plateforme BCITI | ATNA. Elle comprend une demande, vérifie l’information, recommande une réponse ou une action et orchestre les prochaines étapes avec vos systèmes et vos équipes. Ce n’est pas un simple assistant conversationnel : elle s’appuie sur l’identité, le contexte et les règles de votre organisation.',
      en: 'ATNA is the intelligence layer of the BCITI | ATNA platform. It understands a request, verifies the information, recommends an answer or action and orchestrates the next steps with your systems and teams. It is not just a chat assistant: it relies on your organization’s identity, context and rules.',
    },
    link: { to: '/atna', label: { fr: 'Découvrir ATNA', en: 'Discover ATNA' } },
  },
  {
    id: 'replace',
    q: { fr: 'BCITI | ATNA remplace-t-elle les systèmes existants ?', en: 'Does BCITI | ATNA replace existing systems?' },
    a: {
      fr: 'Pas nécessairement. Ce qui fonctionne est connecté ; ce qui crée des coûts ou des redondances inutiles peut être remplacé par un module BCITI | ATNA ; ce qui peut être automatisé l’est avec ATNA. La décision se prend système par système.',
      en: 'Not necessarily. What works is connected; what creates unnecessary cost or redundancy can be replaced by a BCITI | ATNA module; what can be automated is automated with ATNA. The decision is made system by system.',
    },
  },
  {
    id: 'connect',
    q: { fr: 'Comment BCITI | ATNA connecte-t-elle les systèmes existants ?', en: 'How does BCITI | ATNA connect existing systems?' },
    a: {
      fr: 'Par des connecteurs et des API qui relient vos ERP, CRM, systèmes financiers et métiers, données ouvertes et capteurs à la même fondation d’identité et de contexte. Chaque interaction enrichit ensuite ce contexte partagé.',
      en: 'Through connectors and APIs that link your ERP, CRM, finance and line-of-business systems, open data and sensors to the same identity and context foundation. Every interaction then enriches that shared context.',
    },
  },
  {
    id: 'verify',
    q: { fr: 'Comment ATNA vérifie-t-elle l’information ?', en: 'How does ATNA verify information?' },
    a: {
      fr: 'Avant de recommander ou d’agir, ATNA vérifie l’identité de la personne, le contexte autorisé, les sources officielles et les règles applicables, puis évalue un niveau de confiance. Les actions restent soumises aux autorisations et à la supervision humaine, et chaque étape laisse une trace.',
      en: 'Before recommending or acting, ATNA checks the person’s identity, the authorized context, official sources and applicable rules, then assesses a confidence level. Actions remain subject to authorization and human oversight, and every step leaves a trace.',
    },
    link: { to: '/trust', label: { fr: 'Confiance et gouvernance', en: 'Trust & governance' } },
  },
  {
    id: 'trusted',
    q: { fr: 'Qu’est-ce qu’une intelligence de confiance ?', en: 'What is trusted intelligence?' },
    a: {
      fr: 'Une intelligence qui ne devine pas. Elle s’appuie sur une identité vérifiée, un contexte autorisé et des sources fiables, explique ses réponses lorsque c’est important et demande avant d’agir.',
      en: 'An intelligence that doesn’t guess. It relies on verified identity, authorized context and trusted sources, explains its answers when it matters and asks before acting.',
    },
  },
]
